import React, { useState, useEffect, useRef } from 'react';
import { PaperClipIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase-auth';
import type { Task } from '../TaskSidePanel';

interface Message {
  id: string;
  task_id: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  content: string;
  attachments?: Array<{
    name: string;
    size: number;
    url: string;
  }>;
  created_at: string;
  mentions?: string[];
}

interface MessagesTabProps {
  task: Task;
}

export default function MessagesTab({ task }: MessagesTabProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMessages();
    subscribeToMessages();
  }, [task.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('task_messages')
        .select(`
          *,
          sender:users!sender_id (
            id,
            name,
            email,
            avatar_url
          )
        `)
        .eq('task_id', task.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Transform data to match our interface
      const transformedMessages = (data || []).map(msg => ({
        id: msg.id,
        task_id: msg.task_id,
        user_id: msg.sender_id,
        user_name: msg.sender?.name || 'Unknown User',
        user_avatar: msg.sender?.avatar_url,
        content: msg.message || msg.content,
        attachments: msg.attachments,
        created_at: msg.created_at,
        mentions: msg.mentions,
      }));
      
      setMessages(transformedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToMessages = () => {
    const channel = supabase
      .channel(`task-messages-${task.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'task_messages',
          filter: `task_id=eq.${task.id}`,
        },
        (payload) => {
          // Add the new message to the list
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      // Upload attachments if any
      const uploadedAttachments = [];
      for (const file of attachments) {
        const fileName = `${task.id}/${Date.now()}-${file.name}`;
        const { data, error } = await supabase.storage
          .from('message-attachments')
          .upload(fileName, file);

        if (!error && data) {
          uploadedAttachments.push({
            name: file.name,
            size: file.size,
            url: data.path,
          });
        }
      }

      // Extract mentions from message
      const mentions = newMessage.match(/@\w+/g) || [];

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Send message
      const { error } = await supabase.from('task_messages').insert({
        task_id: task.id,
        sender_id: user?.id || 'current_user_id', // Use actual user ID
        message: newMessage,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
        mentions: mentions.length > 0 ? mentions : null,
        is_internal: false,
      });

      if (error) throw error;

      // Clear form
      setNewMessage('');
      setAttachments([]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Mock data for demonstration
  const mockMessages: Message[] = [
    {
      id: '1',
      task_id: task.id,
      user_id: '1',
      user_name: 'John Smith',
      content: 'I\'ve uploaded the initial investment policy statement for review. @sarah could you take a look?',
      created_at: '2024-01-15T10:30:00Z',
      mentions: ['@sarah'],
    },
    {
      id: '2',
      task_id: task.id,
      user_id: '2',
      user_name: 'Sarah Johnson',
      content: 'Thanks @john, I\'ll review it today. The risk assessment looks good so far.',
      created_at: '2024-01-15T11:45:00Z',
      mentions: ['@john'],
    },
    {
      id: '3',
      task_id: task.id,
      user_id: '1',
      user_name: 'John Smith',
      content: 'Great! Let me know if you need any clarifications on the asset allocation section.',
      created_at: '2024-01-15T14:20:00Z',
      attachments: [
        {
          name: 'Asset_Allocation_Details.xlsx',
          size: 45000,
          url: '#',
        },
      ],
    },
  ];

  const displayMessages = loading ? [] : (messages.length > 0 ? messages : mockMessages);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {displayMessages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          displayMessages.map((message) => (
            <div key={message.id} className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {message.user_avatar ? (
                  <img
                    className="h-10 w-10 rounded-full"
                    src={message.user_avatar}
                    alt={message.user_name}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-600 font-medium">
                      {message.user_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline space-x-2">
                  <p className="text-sm font-medium text-gray-900">
                    {message.user_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(message.created_at), 'MMM d, h:mm a')}
                  </p>
                </div>
                <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
                  {message.content.split(/(@\w+)/g).map((part, index) => {
                    if (part.startsWith('@')) {
                      return (
                        <span key={index} className="text-amber-600 font-medium">
                          {part}
                        </span>
                      );
                    }
                    return part;
                  })}
                </div>
                {message.attachments && message.attachments.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {message.attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-2 text-sm"
                      >
                        <PaperClipIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-700">{attachment.name}</span>
                        <span className="text-gray-500">
                          ({formatFileSize(attachment.size)})
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4">
        {attachments.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {attachments.map((file, index) => (
              <div
                key={index}
                className="inline-flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-1 text-sm"
              >
                <PaperClipIcon className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex items-end space-x-2">
          <div className="flex-1">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message... Use @ to mention someone"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-900"
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <PaperClipIcon className="h-5 w-5" />
            </button>
            <button
              type="submit"
              disabled={sending || (!newMessage.trim() && attachments.length === 0)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <PaperAirplaneIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}